import { inject, Injectable } from '@angular/core';

import { SharedLanguageService, TranslateOrDefault } from '@language';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { MediaType, RecordType } from '../../records/entities/metadata/metadata.entity';
import { ApiRecordType } from '../entities/api-record-type.entity';
import { getRecordTypeOptionLabel } from '../helper/record-type-label.helper';
import { RecordTypesService } from './record-types.service';

@Injectable({ providedIn: 'root' })
export class RecordTypeTranslationService {
    private static readonly MEDIA_TYPES = new Set(Object.values(MediaType));

    private readonly recordTypesService: RecordTypesService = inject(RecordTypesService);
    private readonly translationService: SharedLanguageService = inject(SharedLanguageService);

    getActiveRecordTypeOptions(): FormSelectOption[] {
        const recordTypes: ApiRecordType[] = this.recordTypesService.getActiveRecordTypes();

        const locale = this.translationService.getCurrentLocale();

        return recordTypes.map(recordType => ({
            id: recordType.Name,
            value: recordType.Name,
            label: getRecordTypeOptionLabel(recordType, locale, recordTypes)
        }));
    }

    getRecordTypeTranslation(recordType: RecordType, type?: MediaType): string {
        // Handle Record type with MediaType
        if (recordType === RecordType.Record && type) {
            return TranslateOrDefault(this.translationService, `fragments.types.${type}`);
        }

        // Check for manual override translation
        const overrideTranslationKey: string = `overrides.RecordType.${recordType}`;
        const overrideTranslation: string = this.translationService.translate(overrideTranslationKey);

        // Manual translation provided. Default to that translation
        if (overrideTranslation !== overrideTranslationKey) {
            return overrideTranslation;
        }

        // Parse record type parts
        const recordTypeStr = recordType.toString();
        const parts = recordTypeStr.split('.');
        const isFragment = recordTypeStr.endsWith('.Fragment');

        const mainTypeLabel = this.getMainTypeLabel(parts);
        const subtypeLabel = this.getSubTypeLabel(parts);
        const fragmentLabel = TranslateOrDefault(this.translationService, 'fragments.types.fragment');

        // Build translation based on subType and fragment status
        if (!subtypeLabel && isFragment) {
            return `${mainTypeLabel} ${fragmentLabel}`;
        }

        if (!subtypeLabel && !isFragment) {
            return mainTypeLabel;
        }

        if (subtypeLabel && isFragment) {
            return `${subtypeLabel} ${fragmentLabel} (${mainTypeLabel})`;
        }

        return `${subtypeLabel} (${mainTypeLabel})`;
    }

    private getMainTypeLabel(parts: string[]) {
        return this.recordTypesService.getRecordTypeTranslation(parts[0], this.translationService.getCurrentLocale());
    }

    private getSubTypeLabel(parts: string[]) {
        if (parts.length <= 1) {
            return null;
        }
        const subtype = parts[1].toLowerCase();
        if (!this.isValidMediaType(subtype)) {
            return null;
        }
        return TranslateOrDefault(this.translationService, `fragments.types.${subtype}`);
    }

    private isValidMediaType(value: string): value is MediaType {
        return RecordTypeTranslationService.MEDIA_TYPES.has(value as MediaType);
    }
}
